import { Badge } from '@/types'

interface BadgeDisplayProps {
  badge: Badge
  size?: 'sm' | 'md' | 'lg'
  showDescription?: boolean
  showEarnedDate?: boolean
  className?: string
}

const sizeClasses = {
  sm: 'w-8 h-8 text-xs',
  md: 'w-12 h-12 text-sm',
  lg: 'w-16 h-16 text-base'
}

const badgeColors = {
  gold: 'bg-gradient-to-br from-yellow-400 to-yellow-600 text-yellow-900 border-yellow-500',
  silver: 'bg-gradient-to-br from-gray-300 to-gray-500 text-gray-900 border-gray-400',
  bronze: 'bg-gradient-to-br from-orange-400 to-orange-600 text-orange-900 border-orange-500',
  blue: 'bg-gradient-to-br from-blue-400 to-blue-600 text-blue-900 border-blue-500',
  green: 'bg-gradient-to-br from-green-400 to-green-600 text-green-900 border-green-500',
  purple: 'bg-gradient-to-br from-purple-400 to-purple-600 text-purple-900 border-purple-500',
  red: 'bg-gradient-to-br from-red-400 to-red-600 text-red-900 border-red-500',
  default: 'bg-gradient-to-br from-gray-400 to-gray-600 text-gray-900 border-gray-500'
}

export function BadgeDisplay({ 
  badge, 
  size = 'md', 
  showDescription = false, 
  showEarnedDate = false,
  className = '' 
}: BadgeDisplayProps) {
  const formatDate = (dateString: string) => {
    const date = new Date(dateString)
    return date.toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'short', 
      day: 'numeric' 
    })
  }

  const isEarned = !!badge.earnedAt
  const isExpired = badge.validUntil && new Date(badge.validUntil) < new Date()

  const badgeColorClass = badgeColors[badge.color as keyof typeof badgeColors] || badgeColors.default

  return (
    <div className={`flex items-center space-x-3 ${className}`}>
      {/* Badge Icon */}
      <div className={`
        relative flex items-center justify-center rounded-full border-2 shadow-lg
        ${sizeClasses[size]}
        ${isEarned ? badgeColorClass : 'bg-gray-200 text-gray-500 border-gray-300'}
        ${isExpired ? 'opacity-50' : ''}
        transition-all duration-200 hover:scale-105
      `}>
        <span className="font-bold">
          {badge.icon}
        </span>
        
        {/* Earned indicator */}
        {isEarned && (
          <div className="absolute -top-1 -right-1 w-4 h-4 bg-green-500 rounded-full flex items-center justify-center">
            <span className="text-white text-xs">✓</span>
          </div>
        )}
        
        {/* Expired indicator */}
        {isExpired && (
          <div className="absolute -top-1 -right-1 w-4 h-4 bg-red-500 rounded-full flex items-center justify-center">
            <span className="text-white text-xs">!</span>
          </div>
        )}
      </div>

      {/* Badge Details */}
      <div className="flex-1 min-w-0">
        <div className="flex items-center space-x-2">
          <h3 className={`font-semibold text-gray-900 dark:text-white ${
            size === 'sm' ? 'text-sm' : size === 'lg' ? 'text-lg' : 'text-base'
          }`}>
            {badge.name}
          </h3>
          
          {!isEarned && (
            <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300">
              Not Earned
            </span>
          )}
          
          {isExpired && (
            <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-300">
              Expired
            </span>
          )}
        </div>

        {showDescription && (
          <p className={`text-gray-600 dark:text-gray-400 mt-1 ${
            size === 'sm' ? 'text-xs' : 'text-sm'
          }`}>
            {badge.description}
          </p>
        )}

        {showEarnedDate && badge.earnedAt && (
          <p className={`text-gray-500 dark:text-gray-500 mt-1 ${
            size === 'sm' ? 'text-xs' : 'text-sm'
          }`}>
            Earned on {formatDate(badge.earnedAt)}
          </p>
        )}

        {badge.validUntil && (
          <p className={`text-gray-500 dark:text-gray-500 mt-1 ${
            size === 'sm' ? 'text-xs' : 'text-sm'
          }`}>
            {isExpired ? 'Expired on' : 'Valid until'} {formatDate(badge.validUntil)}
          </p>
        )}
      </div>
    </div>
  )
}